import { useEffect, useRef, useState, FC } from 'react';
import { SxStyleProp } from 'theme-ui';
import Konva from 'konva';
import { Effect } from 'types/Effects';
import { RGBAObjToFill } from 'utils/konva/color';

const textareaStyles: SxStyleProp = {
  position: 'absolute',
  border: 'none',
  padding: '0px',
  margin: '0px',
  overflow: 'hidden',
  background: 'none',
  outline: 'none',
  resize: 'none',
  transformOrigin: 'left top'
};

type TextareaComponentProps = {
  text: Effect;
  konvaText: Konva.Text;
  handleStopEditing: (groupId: string, textId: string, value: string) => void;
  handleCancelEditing: (textId: string) => void;
};

export const TextareaComponent: FC<TextareaComponentProps> = ({
  konvaText,
  text,
  handleStopEditing,
  handleCancelEditing
}) => {
  const ref = useRef(null);
  const [areaPosition, setAreaPosition] = useState({ x: null, y: null });
  const textarea = ref.current;
  const [value, setValue] = useState(text.text);
  const konvaGroup = konvaText?.getParent();
  const scale = konvaGroup?.scaleX();

  const setTextareaWidth = (newWidth: number) => {
    // some extra fixes on different browsers
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    if (isSafari || isFirefox) {
      newWidth = Math.ceil(newWidth);
    }

    const isEdge = /Edge/.test(navigator.userAgent);
    if (isEdge) {
      newWidth += 1;
    }
    textarea.style.width = `${newWidth}px`;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // on esc do not set value back to node
    if (e.code === 'Escape') {
      handleCancelEditing(konvaText.id());
    }
    setTextareaWidth(konvaText.width() * scale + 2);
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (e.target !== textarea) {
      setValue(textarea.value);
      handleStopEditing(konvaGroup.id(), konvaText.id(), textarea.value);
    }
  };

  useEffect(() => {
    if (!textarea) return;
    textarea.focus();
    textarea.addEventListener('keydown', handleKeyDown);
    setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    }, 0);
    return () => {
      textarea.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', handleOutsideClick);
    };
  }, [textarea]);

  useEffect(() => {
    if (!konvaText) return;

    const textPosition = konvaText.absolutePosition();

    setAreaPosition({
      x: textPosition.x,
      y: textPosition.y
    });
  }, [konvaText]);

  return (
    <textarea
      ref={ref}
      sx={textareaStyles}
      style={
        konvaText && {
          fontFamily: text.font.fontFamily,
          textAlign: 'left',
          color: RGBAObjToFill({ ...text.textColor, a: text.textOpacity }),
          top: `${areaPosition.y}px`,
          left: `${areaPosition.x}px`,
          width: `${konvaText.width() * scale}px`,
          height: `${konvaText.height() * scale}px`,
          fontSize: `${text.fontSize * scale}px`,
          fontStyle: `${text.isItalic ? 'italic' : ''}`,
          fontWeight: text.isBold ? 600 : 400,
          textDecoration: `${text.isUnderline ? 'underline' : ''}`,
          letterSpacing: `${konvaText.letterSpacing() * scale}px`,
          lineHeight: 1,
          position: 'absolute',
          zIndex: 3
        }
      }
      wrap="hard"
      value={value}
      data-test="text-tool_textarea"
      onChange={e => {
        setValue(e.target.value);
        textarea.style.height = `${textarea.scrollHeight}px`;
      }}
    />
  );
};
